<template>
  <Slider ref="slider" @scroll="onScroll" :class="{ 'playing-lottie-animation': isPlayingLottieAnimation }">
    <lottie-vue-player
      ref="lottie"
      :style="{ background: 'transparent', position: 'fixed', top: `${!isLandscape ? offsetLottieAnimation : 0}px`, left: `${isLandscape ? offsetLottieAnimation : 0}px`, width: '100vw', height: '100vh', 'z-index': 1 }"
      loop
      />
    <MobileAppCard v-if="isMobileAppCardVisible" @card:close="onMobileAppCardClose"/>
    <Card
      ref="cards"
      v-for="(item, index) in items"
      :key="item.key" :data-x-cid="item.key"
      :item="item" :selected="selectedIdentifier == item.key"
      :cardAnimationActive="cardAnimationActive(item.key)"
      :class="getCardClass(index)"
      @mouseover.native="onCardMouseOver(item)" @mouseleave.native="onCardMouseLeave(item)"
      @click.native="onCardClick(item)"
    />
  </Slider>
</template>

<script>
  import { CardType, MediaType } from '../services/cards.service';
  import { isNull } from '../utils';
  import { soundList, soundPlayer } from '../utils/sounds';
  import Slider from '@/components/Slider.vue';
  import Card from '@/components/Card.vue';
  import MobileAppCard from '@/components/MobileAppCard.vue';

  export default {
    components: {
      Slider,
      Card,
      MobileAppCard,
    },
    props: {
      items: {
        type: Array,
        required: true,
      },
    },
    data() {
      return {
        isScrolling: false,
        soundCardPlayingId: null,
        horizontalVisibleCardCount: 0,
        selectedIdentifier: 0,
        selectedCoordinates: { x: 0, y: 0 },
        flagUpdatedFocus: false,
        isMobileAppCardVisible: true,
        classFirstItem: 'first-item',
        cardPlayingLottieAnimation: null,
        offsetLottieAnimation: null,
        isLandscape: true,
      };
    },
    computed: {
      isAgeSettingDisplayed() {
        return this.$store.getters['settings/isAgeSettingDisplayed'];
      },
      loadingCards() {
        return this.$store.getters['settings/loadingCards'];
      },
      getBannerState() {
        return this.$store.getters['settings/getBannerState'];
      },
      activeHeaderNav() {
        return this.$store.getters['settings/activeHeaderNav'];
      },
      isPlayingLottieAnimation() {
        return !isNull(this.cardPlayingLottieAnimation);
      },
    },
    methods: {
      setActiveHeaderBarNav(set) {
        this.$store.dispatch('settings/setActiveHeaderBarNav', set);
      },
      setFocusLiveLink(set) {
        this.$store.dispatch('settings/setFocusLiveLink', set);
      },
      setFocusSettings(set) {
        this.$store.dispatch('settings/setFocusSettings', set);
      },
      setHideMobileCard(set) {
        this.$store.dispatch('settings/setHideMobileCard', set);
      },
      triggerScrollRefresh() {
        this.$refs.slider.$refs.scroller.refresh();
      },
      handleKeydown(event) {
        event.preventDefault();
      },
      cardAnimationActive(id) {
        if (this.cardPlayingLottieAnimation === id || this.soundCardPlayingId === id) {
          return true;
        }

        return false;
      },
      getCardClass(index) {
        return index <= 1 ? [this.classFirstItem] : [];
      },
      onCardMouseOver(item) {
        this.selectedIdentifier = item.key;
        const index = this.items.findIndex(this.findIndexLoc);
        if (index % 2 === 0) {
          this.selectedCoordinates = {
            x: index / 2,
            y: 0,
          };
        } else {
          this.selectedCoordinates = {
            x: index / 2 - 0.5,
            y: 1,
          };
        }
      },
      onCardMouseLeave() {
        this.selectedIdentifier = null;
      },
      findIndexLoc(element) {
        return element.key === this.selectedIdentifier;
      },
      onCardClick(item, keyboardAction = false) {
        if (!this.isScrolling || keyboardAction) {
          if (item.type === CardType.Sound) {
            this.playLottieAnimation(item);
            this.playSoundCard(item);
          } else if (item.type === CardType.Folder) {
            this.$store.dispatch('activeItem/setActiveItem', item);
            soundPlayer(soundList.cardOpen);

            if (item.mediaType === MediaType.Video) {
              this.$ga.event('folder', 'open', `${item.identifier}`);
              this.$router.push({
                name: 'folder',
                params: { identifier: item.identifier },
              });
            } else if (item.mediaType === MediaType.MixedGames) {
              this.$ga.event('folder', 'open', `${item.identifier}`);
              this.$router.push({
                name: 'game',
                params: { identifier: item.identifier },
              });
            }
          }
        }
      },
      playSoundCard(item) {
        if (this.soundCardPlayingId == null) {
          this.soundCardPlayingId = item.key;
          soundPlayer(soundList[item.sound], () => {
            this.soundCardPlayingId = null;
          }, true);
        }
      },
      playLottieAnimation(item) {
        this.cardPlayingLottieAnimation = item.key;

        const lottieAnimationObject = require(`../assets/animations/${item.sound}.json`);
        const animationFrames = lottieAnimationObject.op - lottieAnimationObject.ip;
        const animationFramesPerSecond = lottieAnimationObject.fr;
        const animationDuration = (animationFrames / animationFramesPerSecond) * 1000;
        const animationDeducterToPreventLooping = 10;

        this.$refs.lottie.$refs.player.load(JSON.stringify(lottieAnimationObject));
        this.$refs.lottie.togglePlayPause();
        this.$refs.slider.$refs.scroller.scroller.options.scrollingX = false;
        this.$refs.slider.$refs.scroller.scroller.options.scrollingY = false;

        this.isLandscape = window.matchMedia('(orientation: landscape)').matches;
        this.offsetLottieAnimation = this.$refs.slider.lastScrollIndex;

        setTimeout(() => {
          this.$refs.lottie.togglePlayPause();
          this.$refs.slider.$refs.scroller.scroller.options.scrollingX = true;
          this.$refs.slider.$refs.scroller.scroller.options.scrollingY = true;
          this.cardPlayingLottieAnimation = null;
        }, animationDuration - animationDeducterToPreventLooping);
      },
      onScroll(scrollInfo) {
        this.$emit('scroll', scrollInfo);

        setTimeout(() => {
          this.isScrolling = scrollInfo.isScrolling;
        }, 100);
      },
      handleKeyboard(event) {
        if (!this.activeHeaderNav && !this.getBannerState) {
          event.preventDefault();
          const coordinatesDelta = this.getCoordinatesDelta(event);
          const oldCoordinates = {
            x: this.selectedCoordinates ? this.selectedCoordinates.x : 0,
            y: this.selectedCoordinates ? this.selectedCoordinates.y : 0,
          };

          if (this.selectedCoordinates === null) {
            this.selectedCoordinates = { x: 0, y: 0 };
          } else {
            this.selectedCoordinates.x += coordinatesDelta.x;
            this.selectedCoordinates.y += coordinatesDelta.y;
          }

          this.autoScrollIfNeeded(oldCoordinates, this.selectedCoordinates);

          const index = this.selectedCoordinates.x;
          this.selectedIdentifier = this.items[index].key;

          const item = this.getSelectedItem();
          try {
            if (!this.isAgeSettingDisplayed) {
              switch (event.keyCode) {
                case VK_ENTER: {
                  if (item) {
                    this.onCardClick(item, true);
                  }
                }
              }
            }
          } catch (error) {
            switch (event.code) {
              case 'Enter': {
                if (item) {
                  this.onCardClick(item, true);
                }
              }
            }
          }
        }
      },
      getCoordinatesDelta(event) {
        const delta = { x: 0, y: 0 };
        if (!this.activeHeaderNav && !this.getBannerState) {

          try {
            if (!this.isAgeSettingDisplayed && !this.loadingCards) {
              switch (event.keyCode) {
                case VK_UP: {
                  this.setFocusLiveLink(true);
                  this.setActiveHeaderBarNav(true);

                  break;
                }
                case VK_LEFT: {
                  if (this.selectedCoordinates && this.selectedCoordinates.x > 0) {
                    delta.x--;
                  }
                  break;
                }
                case VK_RIGHT: {
                  delta.x++;

                  break;
                }
              }
            }
          } catch (error) {
            switch (event.code) {
              case 'ArrowUp': {
                this.setFocusLiveLink(true);
                this.setActiveHeaderBarNav(true);

                break;
              }
              case 'ArrowLeft': {
                if (this.selectedCoordinates && this.selectedCoordinates.x > 0) {
                  delta.x--;
                }
                break;
              }
              case 'ArrowRight': {
                delta.x++;
                break;
              }
            }
          }
        }

        return delta;
      },
      getSelectedItem() {
        return this.items.find((item) => item.key === this.selectedIdentifier);
      },
      getHorizontalVisibleCardCount() {
        let count = 0;
        const cards = this.$refs.cards;
        if (cards) {
          for (let i = 0; i < cards.length; i += 2) {
            if (!this.isVisibleOnScreen(cards[i].$el)) {
              break;
            }

            count++;
          }
        }

        return count;
      },
      autoScrollIfNeeded(oldCoordinates, newCoordinates) {
        const count = this.horizontalVisibleCardCount;
        const delta = oldCoordinates.x - newCoordinates.x;
        const direction = delta === 0 ? null : delta > 0 ? 'reverse' : 'forward';
        const slider = this.$refs.slider;
        const size = this.$refs.cards && this.$refs.cards.length > 0
            ? (this.$refs.cards[0].$el).offsetWidth + 30 : 0;
        if (newCoordinates.x > count / 2 && direction === 'forward') {
          slider.scrollHorizontally(size);
        } else if (direction === 'reverse') {
          slider.scrollHorizontally(-size);
        }
      },
      isVisibleOnScreen(element) {
        const rect = element.getBoundingClientRect();
        const clientWidth = document.documentElement ? document.documentElement.clientWidth : 0;
        const viewWidth = Math.max(clientWidth, window.innerWidth);
        return !(rect.left < 0 || rect.right - viewWidth >= 0);
      },
      onMobileAppCardClose() {
        this.isMobileAppCardVisible = false;
        this.setHideMobileCard(true);
      },
    },
    mounted() {
      this.setFocusSettings(false);
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('keyup', this.handleKeyboard);
    },
    updated() {
      // Retrieve visible cards count on screen
      if (!this.flagUpdatedFocus && this.items && this.items.length) {
        this.flagUpdatedFocus = true;
        this.selectedIdentifier = this.items[0].key;
        const item = this.getSelectedItem();
      }
      if (this.horizontalVisibleCardCount === 0) {
        this.horizontalVisibleCardCount = this.getHorizontalVisibleCardCount();
      }
    },
    destroyed() {
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('keyup', this.handleKeyboard);
    },
  };
</script>

<style scoped lang="scss">
  .slider:not(.playing-lottie-animation) {
    .vue-lottie-player {
      display: none;
    }
  }
</style>
